import copy from '../../../copy/en/common.json' with { type: 'json' };
import { Dialog as RadixDialog } from "radix-ui";
import { X } from "lucide-react";
import { useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { SessionContext } from "../../auth/SessionProvider.js";
import { Button, IconButton, type ButtonVariant } from "./Button.js";
import { cx } from "./cx.js";
import { useSurfaceSuspended } from "./SurfaceSuspension.js";

/** Radix traps focus and hides outside controls; retain a reachable preview exit. */
function PreviewModalExit() {
  const session = useContext(SessionContext);
  return session?.viewAs ? <div className="preview-modal-exit"><Button size="sm" variant="secondary" onClick={() => session.endViewAs?.()}>{copy.Dialog.returnToAdministrator}</Button></div> : null;
}

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
  closeDisabled?: boolean;
  onCloseAutoFocus?: (event: Event) => void;
  suspended?: boolean;
  titleHidden?: boolean;
}

/** Centered modal dialog (Radix). */
export function Dialog({ open, onOpenChange, title, description, children, footer, wide, className, closeDisabled, onCloseAutoFocus, suspended = false }: DialogProps) {
  const background = useSurfaceSuspended() || suspended;
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog__overlay" hidden={background} data-suspended={background || undefined} />
        <RadixDialog.Content hidden={background} data-suspended={background || undefined} inert={!open || background || undefined} aria-hidden={!open || background || undefined} className={cx("dialog__content", wide && "dialog__content--wide", className)} onCloseAutoFocus={onCloseAutoFocus}>
          <RadixDialog.Title className="dialog__title">{title}</RadixDialog.Title>
          {description ? (
            <RadixDialog.Description className="dialog__description">{description}</RadixDialog.Description>
          ) : (
            <RadixDialog.Description className="visually-hidden">{typeof title === "string" ? title : copy.Dialog.dialog}</RadixDialog.Description>
          )}
          <PreviewModalExit />
          {children}
          {footer ? <div className="dialog__footer">{footer}</div> : null}
          <RadixDialog.Close asChild>
            <IconButton icon={X} label={copy.Dialog.close} size="sm" className="dialog__close" disabled={closeDisabled} />
          </RadixDialog.Close>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Side sheet for detail-in-context / create forms (Radix Dialog positioned right). */
export function Sheet({ open, onOpenChange, title, description, children, footer, wide, className, closeDisabled, onCloseAutoFocus, suspended = false, titleHidden = false, presentation = "sheet" }: DialogProps & { presentation?: "sheet" | "dialog" }) {
  const background = useSurfaceSuspended() || suspended;
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog__overlay" hidden={background} data-suspended={background || undefined} />
        <RadixDialog.Content hidden={background} data-suspended={background || undefined} inert={!open || background || undefined} aria-hidden={!open || background || undefined} className={cx(presentation === "dialog" ? "dialog__content" : "sheet__content", presentation === "sheet" && wide && "sheet__content--wide", className)} onCloseAutoFocus={onCloseAutoFocus}>
          <RadixDialog.Title className={titleHidden ? "visually-hidden" : "dialog__title"}>{title}</RadixDialog.Title>
          {description ? (
            <RadixDialog.Description className="dialog__description">{description}</RadixDialog.Description>
          ) : (
            <RadixDialog.Description className="visually-hidden">{typeof title === "string" ? title : copy.Dialog.panel}</RadixDialog.Description>
          )}
          <PreviewModalExit />
          {children}
          {footer ? <div className="dialog__footer">{footer}</div> : null}
          <RadixDialog.Close asChild>
            <IconButton icon={X} label={copy.Dialog.close} size="sm" className="dialog__close" disabled={closeDisabled} />
          </RadixDialog.Close>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  confirmVariant?: ButtonVariant;
  /** Show an optional free-text operator note while preserving the legacy prop name. */
  requireReason?: boolean;
  onConfirm: (reason?: string) => Promise<void> | void;
  onCloseAutoFocus?: DialogProps["onCloseAutoFocus"];
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = copy.Dialog.confirm,
  confirmVariant = "danger",
  requireReason = false,
  onConfirm,
  onCloseAutoFocus,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();
  const generation = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    if (!open) {
      generation.current += 1;
      pending.current = false;
      setBusy(false);
      setReason("");
      setError(null);
    }
  }, [open]);
  useEffect(() => () => { generation.current += 1; }, []);

  async function confirm() {
    if (!open || pending.current) return;
    pending.current = true;
    const submitted = generation.current;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(requireReason ? reason.trim() || undefined : undefined);
      if (submitted !== generation.current) return;
      onOpenChange(false);
      setReason("");
    } catch (e) {
      if (submitted === generation.current) setError(e instanceof Error ? e.message : copy.Dialog.somethingWentWrong);
    } finally {
      if (submitted === generation.current) {
        pending.current = false;
        setBusy(false);
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!busy) onOpenChange(o);
      }}
      title={title}
      description={description}
      onCloseAutoFocus={onCloseAutoFocus}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>{copy.Dialog.cancel}</Button>
          <Button variant={confirmVariant} onClick={() => void confirm()} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {requireReason ? (
        <div className="field">
          <label className="field__label" htmlFor={reasonId}>{copy.Dialog.reason}<span className="optional">{copy.Dialog.optional}</span>
          </label>
          <textarea
            id={reasonId}
            className="textarea"
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${reasonId}-error` : undefined}
          />
        </div>
      ) : null}
      {error ? (
        <p id={`${reasonId}-error`} className="field__error" role="alert" style={{ marginTop: "var(--space-3)" }}>
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
